import { Stack, Typography } from '@mui/material';
import {
  BackupPolicyType,
  NativeStandardConfigVaultProtectionModeEnum,
  ScheduleType,
  type BackupSchedule,
} from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';

import type { StepProps } from '@/components/wizard/StepProps';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useUser } from '@/contexts/useUser';

import { VaultProtectionOptions } from './AirGappedVaultOptions';
import type { BackupPolicyEditState } from './BackupPolicyCreationFlow';
import {
  isValidSchedule,
  MIN_RETENTION_DAYS_FOR_AIR_GAPPED,
} from './BackupPolicySchedule';
import { isStandardPolicyType } from './backupPoliciesTypes';
import { BackupPolicyScheduleList } from './backupPolicyScheduleList';
import { BackupPolicySettingsStepContainer } from './backupPolicySettingsStepContainer';
import { BackupPolicySupportedDisclaimer } from './customPolicySupportedDisclaimer';
import {
  getDefaultHighFrequencySchedules,
  getDefaultStandardSchedules,
} from './defaultBackupPolicy';
import { ScheduleModeSelector } from './scheduleModeSelector';

const STANDARD_TIMES_PER_DAY = 4;
export const RESTRICTED_POLICY_TIMES_PER_DAY = 96;

export const BackupPolicySettingsStandard = (
  props: StepProps<BackupPolicyEditState>
) => {
  const { EnableAirGappedVault: enableAirGappedVault = false } =
    useFeatureFlags();
  const { user } = useUser();
  const roleMaxRetentionDays =
    user?.selectedRole?.backupPoliciesLimits?.maxRetentionDays || undefined;
  const [useUtc, setUseUtc] = useState(
    !!props.wizardState.schedules?.[0]?.useUtc
  );

  const addSchedule = (scheduleType: ScheduleType) => {
    const vaultId = '';
    const newState = {
      ...props.wizardState,
    };

    newState.schedules = [
      ...(newState.schedules ?? []),
      {
        ...(props.wizardState.policyType ===
        BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY
          ? getDefaultHighFrequencySchedules(vaultId, scheduleType)[0]
          : getDefaultStandardSchedules(vaultId, scheduleType)[0]),
        useUtc,
      },
    ];

    props.setWizardState(newState);
  };

  const updateSchedule = (schedule: BackupSchedule) => {
    const schedules =
      props.wizardState.schedules?.map((s) =>
        s.id === schedule.id ? schedule : s
      ) ?? [];

    props.setWizardState({ ...props.wizardState, schedules });
  };

  const removeSchedule = (schedule: BackupSchedule) => {
    if (props.wizardState.schedules?.length === 1) return;
    props.setWizardState({
      ...props.wizardState,
      schedules: props.wizardState.schedules?.filter(
        (s) => s.id !== schedule.id
      ),
    });
  };

  const duplicateSchedule = (schedule: BackupSchedule) => {
    props.setWizardState({
      ...props.wizardState,
      schedules: [
        ...(props.wizardState.schedules ?? []),
        { ...schedule, id: uuid() },
      ],
    });
  };

  const timesPerDay = isStandardPolicyType(props.wizardState.policyType)
    ? STANDARD_TIMES_PER_DAY
    : RESTRICTED_POLICY_TIMES_PER_DAY;

  useEffect(() => {
    if (
      props.wizardState.vaultProtectionMode ===
      NativeStandardConfigVaultProtectionModeEnum.AirGapped
    ) {
      const needsUpdate = props.wizardState.schedules?.some(
        (s) => s.backupRetention < MIN_RETENTION_DAYS_FOR_AIR_GAPPED
      );
      if (needsUpdate) {
        props.setWizardState((state) => ({
          ...state,
          schedules: state.schedules?.map((s) =>
            s.backupRetention < MIN_RETENTION_DAYS_FOR_AIR_GAPPED
              ? { ...s, backupRetention: MIN_RETENTION_DAYS_FOR_AIR_GAPPED }
              : s
          ),
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.wizardState.vaultProtectionMode]);

  const canGoNext = () => {
    if (!props.wizardState.schedules?.length) {
      return false;
    }

    for (const schedule of props.wizardState.schedules) {
      if (
        !isValidSchedule(
          schedule,
          props.wizardState.policyType,
          roleMaxRetentionDays,
          props.wizardState.vaultProtectionMode
        )
      ) {
        return false;
      }
    }

    return true;
  };

  return (
    <BackupPolicySettingsStepContainer {...props} canGoNext={canGoNext}>
      <BackupPolicySupportedDisclaimer
        policyType={props.wizardState.policyType}
      />
      <Stack className='px-[min(100px,5vw)] pt-[min(60px,3vw)]'>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          sx={{ mb: '16px' }}
        >
          <Typography variant='body1'>Backup schedules</Typography>
          <ScheduleModeSelector
            wizardState={props.wizardState}
            setWizardState={props.setWizardState}
            useUtc={useUtc}
            setUseUtc={setUseUtc}
            timesPerDay={timesPerDay}
          />
        </Stack>
        <BackupPolicyScheduleList
          policyType={props.wizardState.policyType}
          vaultProtectionMode={props.wizardState.vaultProtectionMode}
          schedules={
            props.wizardState.schedules?.filter(
              (x) => x.scheduleType === ScheduleType.SCHEDULE_TYPE_STANDARD
            ) ?? []
          }
          deleteAllowed={
            (props.wizardState.schedules?.filter(
              (x) => x.scheduleType === ScheduleType.SCHEDULE_TYPE_STANDARD
            ).length ?? 0) > 1
          }
          onUpdate={updateSchedule}
          onDuplicate={duplicateSchedule}
          onDelete={removeSchedule}
          mode={props.wizardState.mode}
          onAdd={() => addSchedule(ScheduleType.SCHEDULE_TYPE_STANDARD)}
          addButtonText='Add Backup Schedule'
        />
        {enableAirGappedVault &&
          props.wizardState.policyType ===
            BackupPolicyType.BACKUP_POLICY_TYPE_NATIVE_STANDARD && (
            <Stack className='mt-[28px] max-w-[800px]'>
              <VaultProtectionOptions
                vaultProtectionMode={
                  props.wizardState.vaultProtectionMode ??
                  NativeStandardConfigVaultProtectionModeEnum.None
                }
                onModeChange={(mode) =>
                  props.setWizardState((state) => ({
                    ...state,
                    vaultProtectionMode: mode,
                  }))
                }
                isLocked={
                  !!props.wizardState.id &&
                  (props.wizardState.vaultProtectionMode ??
                    NativeStandardConfigVaultProtectionModeEnum.None) !==
                    NativeStandardConfigVaultProtectionModeEnum.None
                }
              />
            </Stack>
          )}
      </Stack>
    </BackupPolicySettingsStepContainer>
  );
};
